/**
 * Тактильная отдача.
 *
 * Словарь намеренно СЕМАНТИЧЕСКИЙ: компонент просит `select` или `hold`, а не
 * «вибрацию на 8 мс». Одна роль требует разных вызовов на разных площадках, и
 * решать это в каждом компоненте — верный способ получить разнобой.
 *
 * Площадок три:
 *   • наше Flutter-приложение — обработчик `vibrate` моста flutter_inappwebview
 *     (`{ type }`: selection, light, medium, heavy, success, warning, error);
 *     оболочка отдаёт это HapticFeedback телефона — на iOS тоже;
 *   • Telegram Mini App — штатный HapticFeedback: работает и на iOS, и на
 *     Android, различает силу и тип события. Лучший движок, доступный в вебе.
 *   • обычный браузер — Vibration API. Только Android; iOS Safari вибрацию из
 *     веба не отдаёт вовсе. Силу задать нельзя, поэтому роль кодируется
 *     длительностью и рисунком импульсов.
 *
 * ПОЛИТИКА: отдача — редкое событие, а не фон. Обычные нажатия (кнопки, ряды,
 * чипы, табы) НЕ вибрируют. Отдача остаётся там, где палец иначе не получит
 * информации: клавиши кода, удержание, порог жеста, результат операции.
 * Не добавляй отдачу новым кнопкам «для солидности».
 *
 * Вызовы никогда не бросают: отдача не должна ломать обработчик.
 */
export type HapticRole =
  | 'none'
  | 'select'
  | 'key'
  | 'hold'
  | 'bump'
  | 'soft'
  | 'toggleOn'
  | 'toggleOff'
  | 'success'
  | 'warning'
  | 'error'

interface TelegramHaptics {
  impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void
  notificationOccurred(type: 'error' | 'success' | 'warning'): void
  selectionChanged(): void
}

function telegramHaptics(): TelegramHaptics | undefined {
  return (window as unknown as { Telegram?: { WebApp?: { HapticFeedback?: TelegramHaptics } } })
    .Telegram?.WebApp?.HapticFeedback
}

/**
 * Vibration API отдаёт вибрацию только после первого жеста пользователя и
 * только на видимой странице; иначе вызов игнорируется, а Firefox ещё и пишет
 * предупреждение в консоль — поэтому проверяем сами.
 */
function buzz(pattern: number | number[]): void {
  if (document.visibilityState !== 'visible') return
  navigator.vibrate?.(pattern)
}

/** Тип отдачи для Flutter-оболочки — те же роли, что у Telegram, по-своему названные. */
export type NativeHaptic =
  | 'selection'
  | 'light'
  | 'medium'
  | 'heavy'
  | 'success'
  | 'warning'
  | 'error'

const NATIVE: Record<Exclude<HapticRole, 'none'>, NativeHaptic> = {
  select: 'selection',
  key: 'light',
  hold: 'medium',
  bump: 'heavy',
  soft: 'light',
  toggleOn: 'light',
  toggleOff: 'light',
  success: 'success',
  warning: 'warning',
  error: 'error',
}

interface FlutterBridge {
  callHandler(name: string, ...args: unknown[]): Promise<unknown>
}

function flutterBridge(): FlutterBridge | undefined {
  const bridge = (window as unknown as { flutter_inappwebview?: FlutterBridge })
    .flutter_inappwebview
  return typeof bridge?.callHandler === 'function' ? bridge : undefined
}

const ROLES: Record<Exclude<HapticRole, 'none'>, (h: TelegramHaptics | undefined) => void> = {
  /** тик выбора в пикере — самый слабый из всех */
  select: (h) => (h ? h.selectionChanged() : buzz(6)),
  /** клавиша кода: по ним бьют подряд, отдача сухая и короткая */
  key: (h) => (h ? h.impactOccurred('light') : buzz(8)),
  /** сработало удержание — другое событие, палец должен это понять */
  hold: (h) => (h ? h.impactOccurred('medium') : buzz(18)),
  /** пройден порог жеста */
  bump: (h) => (h ? h.impactOccurred('rigid') : buzz(12)),
  soft: (h) => (h ? h.impactOccurred('soft') : buzz(7)),
  /** две стороны переключателя различаются рисунком: силу задать нельзя */
  toggleOn: (h) => (h ? h.impactOccurred('light') : buzz(10)),
  toggleOff: (h) => (h ? h.impactOccurred('soft') : buzz([6, 24, 6])),
  success: (h) => (h ? h.notificationOccurred('success') : buzz([12, 40, 12])),
  warning: (h) => (h ? h.notificationOccurred('warning') : buzz([16, 60, 16])),
  error: (h) => (h ? h.notificationOccurred('error') : buzz([20, 50, 20, 50, 20])),
}

/** Единственная точка входа. Зовётся из обработчиков касания — обязана быть дешёвой. */
export function haptic(role: HapticRole = 'none'): void {
  if (role === 'none') return
  try {
    const flutter = flutterBridge()
    if (flutter) {
      // Ответ не ждём: отдача — не результат, и отказ оболочки ничего не ломает.
      flutter.callHandler('vibrate', { type: NATIVE[role] }).catch(() => undefined)
      return
    }
    ROLES[role](telegramHaptics())
  } catch {
    /* нет API или отказ площадки — просто без вибрации */
  }
}
